import { Tabs } from "antd";
import { Content } from "antd/lib/layout/layout";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import GetPositionPerformanceApi from "../../api/individuals/GetPositionPerformanceApi";
import GetMyPlansApi from "../../api/plans/GetMyPlansApi";
import { GetPlanTemplatesApi } from "../../api/plan-templates/GetPlanTemplatesApi";
import DashboardBreadcrumb from "../../components/layout/DashboardBreadcrumb";
import PositionPerformanceOverview from "../../components/positionPerformance/PositionPerformanceOverview";
import PositionTasksTab from "../../components/PositionTasksTab";
import MyPlansTabContent from "../../components/MyPlansTabContent";
import PageLoader from "../../components/sub/PageLoader";
import { LIMIT_PER_PAGE } from "../../helpers/constants";

const { TabPane } = Tabs;

function PositionPerformance() {
  const proHeight = document.body.clientHeight;
  const [positionData, setPositionData] = useState();
  const [templates, setTemplates] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [planData, setPlanData] = useState([]);
  const [planCount, setPlanCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [canStart, setCanStart] = useState(false);

  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const positionState = useSelector((state) => state.positionState.value);
  const curLocation = useLocation();

  function getPositionId() {
    const path = curLocation.pathname;
    const id = path.split("/positions-performance/");
    return id[1];
  }

  useEffect(() => {
    getPositionsData();
    loadTemplates();
  }, [curLocation, perfCycleState]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadTemplates() {
    const apiResponse = await GetPlanTemplatesApi();
    if (apiResponse.success) {
      setTemplates(apiResponse.data);
      setCanStart(true);
    }
  }

  async function getPositionsData() {
    const positionId = getPositionId();
    const apiResponse = await GetPositionPerformanceApi(positionId, perfCycleState.id);
    if (apiResponse.message === "success") {
      setPositionData(apiResponse.data);
    }
  }

  const selectedTemplate = templates?.find(
    (t) => t.id === perfCycleState.template
  );

  const sortedPlanLevels = selectedTemplate?.planLevels
    ?.slice()
    .sort((a, b) => a.order - b.order) || [];

  const hasTasksLevel = sortedPlanLevels.some((l) => l.tasks);

  async function loadPlansForLevel(planLevelId, pageNum = 1, newSet = true) {
    setTableLoading(true);
    const apiResponse = await GetMyPlansApi(
      perfCycleState.id,
      planLevelId,
      pageNum,
      LIMIT_PER_PAGE,
      getPositionId()
    );
    if (apiResponse.success) {
      if (newSet) {
        setPlanData(apiResponse.data?.plans || []);
        setPlanCount(apiResponse.data?.totalCount || 0);
      } else {
        setPlanData((prev) => [...prev, ...(apiResponse.data?.plans || [])]);
      }
      setPage(pageNum);
    }
    setTableLoading(false);
    setLoading(false);
  }

  function handleTabChange(key) {
    setActiveTab(key);
    // If it's a plan level tab, fetch data for it
    const planLevel = sortedPlanLevels.find((l) => l.id === key);
    if (planLevel) {
      setLoading(true);
      setPlanData([]);
      loadPlansForLevel(planLevel.id, 1, true);
    }
  }

  const left = (
    <div className="flex items-baseline">
      <Link to="/positions-performance">
        <div className="text-sm pri-font text-gray-400 hover:text-gray-500">
          Positions
        </div>
      </Link>
      <div className="mx-1 text-lg">/</div>
      <div className="text-lg pri-font max-w-xs truncate">
        {positionData
          ? `${positionData?.designation?.abbreviation} (${positionData?.unit?.name})`
          : "..."}
      </div>
    </div>
  );

  return (
    <>
      <Content style={{ backgroundColor: "#fff" }}>
        <DashboardBreadcrumb left={left} />
        <div
          style={{ height: proHeight - 122, overflow: "auto" }}
          className="custom-scroll"
        >
          {canStart ? (
            <Tabs
              defaultActiveKey="overview"
              activeKey={activeTab}
              centered
              onChange={handleTabChange}
              tabBarGutter={40}
            >
              {/* Overview tab */}
              <TabPane
                tab={<div className="w-32 text-center">Overview</div>}
                key="overview"
              >
                <div className="px-6">
                  <PositionPerformanceOverview />
                </div>
              </TabPane>

              {/* Dynamic plan level tabs */}
              {sortedPlanLevels.map((planLevel) => (
                <TabPane
                  tab={<div className="w-32 text-center">{planLevel.name}</div>}
                  key={planLevel.id}
                >
                  <div className="mb-8">
                    <MyPlansTabContent
                      planLevel={planLevel}
                      data={planData}
                      page={page}
                      loading={loading}
                      tableLoading={tableLoading}
                      loadMore={(num) => loadPlansForLevel(planLevel.id, num, false)}
                      setPage={(num) => setPage(num)}
                      planCount={planCount}
                      perfCycleId={perfCycleState.id}
                      positionId={getPositionId()}
                      dataLength={planData.length}
                    />
                  </div>
                </TabPane>
              ))}

              {/* Tasks tab */}
              {hasTasksLevel && (
                <TabPane
                  tab={<div className="w-32 text-center">Tasks</div>}
                  key="tasks"
                >
                  <div className="px-6">
                    <PositionTasksTab
                      positionId={getPositionId()}
                      perfCycleId={perfCycleState.id}
                    />
                  </div>
                </TabPane>
              )}
            </Tabs>
          ) : (
            <PageLoader />
          )}
        </div>
      </Content>
    </>
  );
}

export default PositionPerformance;
